import {
  Coordinates,
  EnginePointData,
  EngineState,
  MarkNamespace,
  MarkerStyle,
  PointRiskLevel,
  markTypeColor,
} from "@bennara/shared";
import * as THREE from "three";
import { CONFIG } from "../config";
import {
  createPointMarker,
  createPointMarkerMaterial,
} from "./utils/createPointMarker";
import {
  createShapeMarker,
  createShapeMarkerMaterial,
  orientToSurface,
  surfaceNormalAt,
} from "./utils/createShapeMarker";
import { createRiskDot, createRiskDotMaterial } from "./utils/createRiskDot";
import { FocusedPointRing } from "./FocusedPointRing";
import { getRiskDotColor } from "./utils/getRiskDotColor";
import { MovingMarker } from "./MovingMarker";

export interface PointMarkerUserData {
  pointId: string;
  coordinates: Coordinates;
  riskLevel: PointRiskLevel;
  /** V2 mark type; undefined for V1 points. */
  type?: MarkNamespace.Type;
  noDraftPosition?: boolean;
}

export class PointMarkers {
  private readonly sphereMaterial = createPointMarkerMaterial(
    window.colors.markSphere,
  );

  /** Resting spheres follow the active theme. Placement color stays `moleNew`. */
  applyTheme(): void {
    (this.sphereMaterial as THREE.MeshBasicMaterial).color.set(
      window.colors.markSphere,
    );
    for (const [type, material] of this.shapeMaterials) {
      material.color.set(markTypeColor(type, window.colors));
    }
    this.movingMarker.applyTheme();
  }

  private readonly shapeMaterials = new Map<
    MarkNamespace.Type,
    THREE.MeshBasicMaterial
  >();

  private getShapeMaterial(type: MarkNamespace.Type): THREE.MeshBasicMaterial {
    let material = this.shapeMaterials.get(type);
    if (!material) {
      material = createShapeMarkerMaterial(
        markTypeColor(type, window.colors),
        type,
      );
      this.shapeMaterials.set(type, material);
    }
    return material;
  }

  /** Shared per dot colour so setPoints does not allocate a material per mole. */
  private readonly riskDotMaterials = new Map<string, THREE.SpriteMaterial>();

  private getRiskDotMaterial(color: string): THREE.SpriteMaterial {
    let material = this.riskDotMaterials.get(color);
    if (!material) {
      material = createRiskDotMaterial(color);
      this.riskDotMaterials.set(color, material);
    }
    return material;
  }

  private readonly pointMarkers = new THREE.Group();
  private points: EnginePointData[] = [];
  private markerStyle: MarkerStyle = "spheres";
  private bodyRoot: THREE.Object3D | null = null;
  private engineState: EngineState | null = null;
  private readonly _normal = new THREE.Vector3();

  private selectedPointId: string | null = null;
  private movingMarker = new MovingMarker();
  private readonly movingRing = new FocusedPointRing();

  private pulseTime = 0;
  private lastTimestamp = 0;
  private readonly _dotWorldPos = new THREE.Vector3();
  private readonly _dotCamDir = new THREE.Vector3();
  private readonly _dotLocalDir = new THREE.Vector3();
  private readonly _dotInvMatrix = new THREE.Matrix4();
  private mode: EngineState["interactionMode"] | null = null;

  constructor(scene: THREE.Scene) {
    scene.add(this.pointMarkers);
    scene.add(this.movingRing.getRing());
    scene.add(this.movingMarker.getMarker());

    this.movingRing.hide();
    this.movingMarker.hide();
  }

  private _findSelectedPoint(
    pointId?: string | null,
  ): PointMarkerUserData | null {
    if (!pointId) {
      return null;
    }

    let userData: PointMarkerUserData | null = null;

    this.pointMarkers.traverse((child) => {
      if (child.userData?.pointId === pointId) {
        userData = {
          pointId: child.userData?.pointId,
          coordinates: child.userData?.coordinates ?? {
            x: child.position.x,
            y: child.position.y,
            z: child.position.z,
          },
          riskLevel: child.userData?.riskLevel,
          type: child.userData?.type,
        };
      }
    });

    return userData;
  }

  private _hideOriginalPoint = (pointId?: string | null) => {
    this.pointMarkers.traverse((child) => {
      if (child.userData?.pointId === pointId) {
        child.visible = false;
      }
    });
  };

  private _showAllOriginalPoints = () => {
    this.pointMarkers.traverse((child) => {
      child.visible = true;
    });
  };

  /** Body mesh used to aim shape stamps at the skin. */
  setBody(root: THREE.Object3D): void {
    this.bodyRoot = root;
    if (this.markerStyle === "shapes" && this.points.length > 0) {
      this._rebuild();
      if (this.engineState) this.updateEngineState(this.engineState);
    }
  }

  setMarkerStyle(style: MarkerStyle): void {
    this.markerStyle = style;
    this._rebuild();
    if (this.engineState) this.updateEngineState(this.engineState);
  }

  setPoints(points: EnginePointData[]): void {
    this.points = points;
    this._rebuild();
  }

  private _normalAt(point: Coordinates): THREE.Vector3 {
    if (this.bodyRoot) {
      return surfaceNormalAt(this.bodyRoot, point, this._normal);
    }
    this._normal.set(point.x, point.y, point.z);
    if (this._normal.lengthSq() < 1e-8) this._normal.set(0, 1, 0);
    else this._normal.normalize();
    return this._normal;
  }

  private _rebuild(): void {
    this.pointMarkers.clear();

    for (const point of this.points) {
      const type = point.type;
      const useShape = this.markerStyle === "shapes" && type != null;
      const marker = useShape
        ? createShapeMarker(this.getShapeMaterial(type), type)
        : createPointMarker(this.sphereMaterial);
      const dotColor = getRiskDotColor(point.riskLevel, point.type);
      if (dotColor) {
        marker.add(createRiskDot(this.getRiskDotMaterial(dotColor)));
      }

      if (useShape) {
        orientToSurface(marker, this._normalAt(point.coordinates));
      }
      marker.position.set(
        point.coordinates.x,
        point.coordinates.y,
        point.coordinates.z,
      );

      marker.userData = {
        pointId: point.id,
        coordinates: point.coordinates,
        riskLevel: point.riskLevel,
        type: point.type,
      };

      this.pointMarkers.add(marker);
    }
  }

  getPoint(pointId: string): PointMarkerUserData | null {
    return this._findSelectedPoint(pointId);
  }

  _renderSelectMode(): void {
    this._showAllOriginalPoints();

    if (this.selectedPointId) {
      const selectedPoint = this._findSelectedPoint(this.selectedPointId);
      if (selectedPoint) {
        this.movingRing.setSelectColor();
        this.movingRing.moveToCoordinates(selectedPoint.coordinates);
        this.movingRing.show();
        this.movingMarker.hide();
      }
    } else {
      this.movingMarker.hide();
      this.movingRing.hide();
    }
  }

  _renderEditMode(): void {
    if (this.selectedPointId) {
      const selectedPoint = this._findSelectedPoint(this.selectedPointId);
      if (selectedPoint) {
        this._hideOriginalPoint(this.selectedPointId);
        this.movingRing.setEditColor();

        const movingMarkerUserData = this.movingMarker.getUserData();
        if (movingMarkerUserData) {
          this.movingRing.moveToCoordinates(movingMarkerUserData.coordinates);
          this.movingRing.show();
          this.movingMarker.setPositionAndShow(
            movingMarkerUserData.coordinates,
          );
        }
      }
    } else {
      this.movingMarker.hide();
      this.movingRing.hide();
      this._showAllOriginalPoints();
    }
  }

  _renderAddMode(): void {
    this._showAllOriginalPoints();

    const movingMarkerUserData = this.movingMarker.getUserData();

    if (movingMarkerUserData?.noDraftPosition) {
      this.movingMarker.hide();
      this.movingRing.hide();
    } else {
      if (movingMarkerUserData) {
        this.movingRing.setEditColor();
        this.movingRing.moveToCoordinates(movingMarkerUserData.coordinates);
        this.movingMarker.show();
        this.movingRing.show();
      }
    }
  }

  _render(): void {
    switch (this.mode) {
      case "select":
        this._renderSelectMode();
        break;
      case "edit":
        this._renderEditMode();
        break;
      case "add":
        this._renderAddMode();
        break;
    }
  }
  private timeout: ReturnType<typeof setTimeout> | null = null;

  updateEngineState(state: EngineState): void {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    this.engineState = state;
    this.mode = state.interactionMode;
    this.selectedPointId = state.selectedPointId;

    const selectedPoint = this._findSelectedPoint(this.selectedPointId);
    if (selectedPoint) {
      this.movingMarker.setUserData({
        pointId: selectedPoint.pointId,
        coordinates: selectedPoint.coordinates,
        riskLevel: selectedPoint.riskLevel,
        type: selectedPoint.type,
      });
    }

    // this.timeout = setTimeout(() => {
    //   alert(
    //     JSON.stringify(
    //       { state, selectedPoint, movingMarker: this.movingMarker.userData },
    //       null,
    //       2,
    //     ),
    //   );
    // }, 1000);

    if (state.draftPosition) {
      this.movingMarker
        .getPosition()
        .set(
          state.draftPosition.coordinates.x,
          state.draftPosition.coordinates.y,
          state.draftPosition.coordinates.z,
        );
      this.movingMarker.setUserData({
        pointId: this.movingMarker.getUserData()?.pointId ?? "",
        riskLevel:
          this.movingMarker.getUserData()?.riskLevel ?? PointRiskLevel.NORMAL,
        coordinates: state.draftPosition.coordinates,
        type:
          state.draftType ??
          selectedPoint?.type ??
          this.movingMarker.getUserData()?.type,
      });
    } else {
      this.movingMarker.setUserData({
        pointId: this.movingMarker.getUserData()?.pointId ?? "",
        riskLevel:
          this.movingMarker.getUserData()?.riskLevel ?? PointRiskLevel.NORMAL,
        coordinates: this.movingMarker.getUserData()?.coordinates ?? {
          x: 0,
          y: 0,
          z: 0,
        },
        type:
          state.draftType ??
          selectedPoint?.type ??
          this.movingMarker.getUserData()?.type,
        noDraftPosition: true,
      });
    }

    const previewType =
      state.draftType ??
      selectedPoint?.type ??
      this.movingMarker.getUserData()?.type;
    this.movingMarker.configure(this.markerStyle, previewType ?? undefined);
    if (
      this.markerStyle === "shapes" &&
      previewType &&
      this.movingMarker.getUserData()?.coordinates
    ) {
      this.movingMarker.orient(
        this._normalAt(this.movingMarker.getUserData()!.coordinates),
      );
    } else {
      this.movingMarker.orient(null);
    }

    this._render();
  }
  /**
   * Scale all point markers based on zoom (camera distance).
   * Keeps markers at roughly constant screen size when zooming.
   */
  setPointScale(zoom: number, camera: THREE.Camera): void {
    const rawScale = zoom * CONFIG.marker.sizeScaleFactor;
    const scale = THREE.MathUtils.clamp(rawScale, 0.1, 0.4);
    // Sit the dot just outside the sphere, toward the camera, so the ball
    // does not cover it and the body still hides it when the body is closer.
    const dotOffset = CONFIG.marker.radius * 1.08;
    this.pointMarkers.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;

      child.scale.setScalar(scale);
      child.updateWorldMatrix(true, false);
      child.getWorldPosition(this._dotWorldPos);
      this._dotCamDir.copy(camera.position).sub(this._dotWorldPos).normalize();
      this._dotInvMatrix.copy(child.matrixWorld).invert();
      this._dotLocalDir
        .copy(this._dotCamDir)
        .transformDirection(this._dotInvMatrix);

      for (const nested of child.children) {
        if (!(nested instanceof THREE.Sprite)) continue;
        nested.position.copy(this._dotLocalDir).multiplyScalar(dotOffset);
      }
    });
    this.movingMarker.getMarker().scale.setScalar(scale);
    this.movingRing
      .getRing()
      .scale.setScalar(scale * CONFIG.marker.ring.scaleFactor);

    this._updatePulse();
  }

  private _updatePulse(): void {
    if (!this.movingRing.getRing().visible) return;

    const now = performance.now() / 1000;
    if (this.lastTimestamp === 0) {
      this.lastTimestamp = now;
    }
    const dt = now - this.lastTimestamp;
    this.lastTimestamp = now;

    const { pulseMin, pulseMax, pulseSpeed } = CONFIG.marker.ring;
    this.pulseTime += dt * pulseSpeed;

    const t = (Math.sin(this.pulseTime * Math.PI * 2) + 1) / 2;
    const material = this.movingRing.getRing().material as THREE.SpriteMaterial;
    material.opacity = pulseMin + t * (pulseMax - pulseMin);
  }
}
